import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import SEO from '../components/SEO';
import { SearchX, Home, BookOpen, Play, Brain, Sparkles, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get SEO-friendly content URL
const getContentUrl = (item) => `/content/${item.slug || item.content_id}`;

const getContentIcon = (type) => {
  switch (type) {
    case 'video': return Play;
    case 'knowledge': return Brain;
    case 'article': return BookOpen;
    default: return BookOpen;
  }
};

export default function NotFound() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch popular content suggestions
    fetch(`${BACKEND_URL}/api/content/suggestions/popular?limit=6`)
      .then(res => res.json())
      .then(data => {
        setSuggestions(data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="not-found-page">
      <SEO 
        title="Page Not Found"
        description="The page you're looking for doesn't exist. Explore our popular skincare and haircare content instead."
        robots="noindex, nofollow"
      />
      <Navbar />
      
      <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* 404 Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gold/10 mb-6">
              <SearchX className="w-12 h-12 text-gold" />
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold mb-4">
              Page Not Found
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
              Oops! The page you're looking for seems to have wandered off. 
              Let's get you back on track with some great content.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/">
                <Button className="btn-gold gap-2" data-testid="go-home-btn">
                  <Home className="w-4 h-4" />
                  Go Home
                </Button>
              </Link>
              <Link to="/daily-drop">
                <Button variant="outline" className="gap-2" data-testid="browse-content-btn">
                  <BookOpen className="w-4 h-4" />
                  Browse Content
                </Button>
              </Link>
            </div>
          </div>

          {/* Suggested Content Section */}
          <div className="border-t pt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl font-semibold flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-gold" />
                Popular Content You Might Like
              </h2>
              <Link 
                to="/deep-dives" 
                className="text-sm text-gold hover:underline flex items-center gap-1"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <Card key={i} className="bg-card border-none shadow-card">
                    <Skeleton className="aspect-video" />
                    <CardContent className="p-4">
                      <Skeleton className="h-4 w-20 mb-2" />
                      <Skeleton className="h-5 w-full mb-2" />
                      <Skeleton className="h-4 w-3/4" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : suggestions.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {suggestions.map((item) => {
                  const Icon = getContentIcon(item.content_type);
                  return (
                    <Link 
                      key={item.content_id} 
                      to={getContentUrl(item)}
                      className="group"
                      data-testid={`suggestion-${item.content_id}`}
                    >
                      <Card className="bg-card border-none shadow-card card-hover h-full overflow-hidden">
                        <div className="aspect-video overflow-hidden relative">
                          <img
                            src={item.image_url || 'https://images.pexels.com/photos/6925487/pexels-photo-6925487.jpeg'}
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                          {item.content_type === 'video' && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                              <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                                <Play className="w-5 h-5 text-primary fill-current ml-0.5" />
                              </div>
                            </div>
                          )}
                        </div>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className="w-3 h-3 text-gold" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-gold">
                              {item.category}
                            </span>
                          </div>
                          <h3 className="font-semibold line-clamp-2 group-hover:text-gold transition-colors mb-2">
                            {item.title}
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {item.summary}
                          </p>
                          <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                            <span>{item.read_time} min {item.content_type === 'video' ? 'watch' : 'read'}</span>
                            <span className="flex items-center gap-1">
                              <Sparkles className="w-3 h-3 text-gold" />
                              +{item.points_reward}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No suggestions available at the moment.</p>
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="mt-12 grid sm:grid-cols-4 gap-4">
            <Link to="/daily-drop" className="group">
              <Card className="bg-secondary/50 border-none hover:bg-secondary transition-colors">
                <CardContent className="p-4 text-center">
                  <BookOpen className="w-6 h-6 mx-auto mb-2 text-gold group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-medium">Daily Drop</span>
                </CardContent>
              </Card>
            </Link>
            <Link to="/video-vault" className="group">
              <Card className="bg-secondary/50 border-none hover:bg-secondary transition-colors">
                <CardContent className="p-4 text-center">
                  <Play className="w-6 h-6 mx-auto mb-2 text-gold group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-medium">Video Vault</span>
                </CardContent>
              </Card>
            </Link>
            <Link to="/knowledge" className="group">
              <Card className="bg-secondary/50 border-none hover:bg-secondary transition-colors">
                <CardContent className="p-4 text-center">
                  <Brain className="w-6 h-6 mx-auto mb-2 text-gold group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-medium">Knowledge</span>
                </CardContent>
              </Card>
            </Link>
            <Link to="/quiz" className="group">
              <Card className="bg-secondary/50 border-none hover:bg-secondary transition-colors">
                <CardContent className="p-4 text-center">
                  <Sparkles className="w-6 h-6 mx-auto mb-2 text-gold group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-medium">Take Quiz</span>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
